// util used by the two PlayerContexts
import { getFirestore, doc, setDoc, increment, serverTimestamp } from "firebase/firestore";
import { app } from "../firebase";

/** 30-second in-memory debounce so re-plays / seeks don’t spam Firestore */
const MEM_DEBOUNCE_MS = 30_000;

/**
 * Extra protection: per-device cooldown persisted across reloads.
 * This prevents refresh / new tab / reconnect spam from re-triggering counters.
 * Set to 30 minutes by default.
 */
const LOCAL_COOLDOWN_MS = 30 * 60_000;

const lastPing = new Map(); // { trackId -> Date.now() }
const db = getFirestore(app);

function getLocalKey(trackId) {
  return `vkz_playCooldown_${trackId}`;
}

function readLastLocal(trackId) {
  try {
    return Number(localStorage.getItem(getLocalKey(trackId)) || 0);
  } catch {
    return 0;
  }
}

function writeLastLocal(trackId, ms) {
  try {
    localStorage.setItem(getLocalKey(trackId), String(ms));
  } catch {
    // ignore if storage blocked (Safari private mode, etc.)
  }
}

/**
 * Fire-and-forget counter increment.
 * Writes to trackStats/{trackId} instead of tracks/{trackId}
 * to prevent Firestore triggers on tracks from firing constantly.
 *
 * @param {string} trackId Firestore document id
 */
export function incrementPlay(trackId) {
  if (!trackId) return;

  const now = Date.now();

  // 1) Per-tab debounce
  const last = lastPing.get(trackId) || 0;
  if (last && now - last < MEM_DEBOUNCE_MS) return;
  lastPing.set(trackId, now);

  // 2) Per-device cooldown (survives reloads / new tabs)
  const lastLocal = readLastLocal(trackId);
  if (lastLocal && now - lastLocal < LOCAL_COOLDOWN_MS) return;
  writeLastLocal(trackId, now);

  // ✅ Write to trackStats (NOT tracks) so your onDocumentUpdated(tracks/{trackId}) triggers stop spamming
  const statsRef = doc(db, "trackStats", trackId);

  // Fire-and-forget (don’t await in the player)
  setDoc(
    statsRef,
    {
      playCount: increment(1),
      lastPlayedAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    },
    { merge: true }
  ).catch((e) => console.error("playCount update failed:", e));
}
